import React, { useState, useEffect, useRef } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { useForm } from 'react-hook-form';
import { Icon } from '@iconify/react/dist/iconify.js';

const customStyles = `
  .form-grid-container {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 1.5rem !important;
    width: 100% !important;
    margin-bottom: 1.25rem !important;
  }
  .form-grid-container > div {
    width: 100 !important;
    box-sizing: border-box !important;
  }
  .form-control, textarea {
    width: 100 !important;
    box-sizing: border-box !important;
    border-radius: 8px !important;
    padding: 8px 12px !important;
    border: 1px solid #ced4da !important;
    background: #fff !important;
    font-size: 0.875rem !important;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
  }
  .form-control:focus, textarea:focus {
    border-color: #1a73e8 !important;
    box-shadow: 0 0 0 0.2rem rgba(26, 115, 232, 0.25) !important;
    outline: none !important;
  }
  textarea {
    height: 120px !important;
    resize: vertical !important;
  }
  .error-message {
    color: #dc3545;
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
  .success-message {
    color: #28a745;
    font-size: 0.875rem;
    margin-bottom: 1rem;
    text-align: center;
  }
  .permissions-group {
    background: #f8fafc;
    padding: 1rem;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    height: 100%;
  }
  .permissions-group label {
    font-weight: 500;
    color: #1a73e8;
  }
  .checkbox-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  .form-check {
    margin-bottom: 0 !important;
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
  }
  .form-check-input[type="checkbox"] {
    appearance: none !important;
    -webkit-appearance: none !important;
    width: 16px !important;
    height: 16px !important;
    border: 2px solid #4a5568 !important;
    border-radius: 4px !important;
    display: inline-block !important;
    position: relative !important;
    cursor: pointer !important;
    background-color: #fff !important;
    transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out !important;
  }
  .form-check-input[type="checkbox"]:checked {
    background-color: #1a73e8 !important;
    border-color: #1a73e8 !important;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3E%3C/svg%3E") !important;
    background-size: 12px !important;
    background-position: center !important;
    background-repeat: no-repeat !important;
  }
  .form-check-input[type="checkbox"]:focus {
    box-shadow: 0 0 0 0.2rem rgba(26, 115, 232, 0.25) !important;
    outline: none !important;
  }
  .form-check label {
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    color: #4a4a4a !important;
    margin-bottom: 0 !important;
    cursor: pointer !important;
  }
  /* Prevent text color from changing when clicking */
  .form-check-input:checked + label {
    color: #4a4a4a !important;
  }
  .form-label {
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    color: #4a4a4a !important;
    margin-bottom: 0.5rem !important;
  }
  .card-body {
    overflow-y: auto !important;
    max-height: calc(100vh - 80px) !important;
    padding: 1.5rem !important;
    overscroll-behavior: contain !important;
  }
  .close-btn {
    position: absolute !important;
    top: 1rem !important;
    right: 1rem !important;
    z-index: 10 !important;
  }
`;

const AddAdminUser = ({ isOpen, onClose, onSubmit, userToEdit }) => {
  const [message, setMessage] = useState(null);
  const nameInputRef = useRef(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const loggedInUser = JSON.parse(localStorage.getItem("user"));
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const { register, handleSubmit, formState: { errors }, reset, setValue, clearErrors } = useForm({
    defaultValues: {
      name: userToEdit?.name || '',
      surname: userToEdit?.surname || '',
      email: userToEdit?.email || '',
      phone: userToEdit?.phone || '',
      address: userToEdit?.address || '',
      status: userToEdit?.status === 'Active',
      userManagement: userToEdit?.permissions?.userManagement || false,
      cropManagement: userToEdit?.permissions?.cropManagement || false,
      reportAccess: userToEdit?.permissions?.reportAccess || false,
      deleteAllowed: userToEdit?.permissions?.deleteAllowed || false,
      password: userToEdit?.password || '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      }, 100);
    }
    if (userToEdit) {
      setValue('name', userToEdit.name || '');
      setValue('surname', userToEdit.surname || '');
      setValue('email', userToEdit.email || '');
      setValue('phone', userToEdit.phone || '');
      setValue('address', userToEdit.address || '');
      setValue('status', userToEdit.status === 'Active');
      setValue('userManagement', userToEdit.permissions?.userManagement || false);
      setValue('cropManagement', userToEdit.permissions?.cropManagement || false);
      setValue('reportAccess', userToEdit.permissions?.reportAccess || false);
      setValue('deleteAllowed', userToEdit.permissions?.deleteAllowed || false);
      setValue('password', userToEdit.password || '');
      clearErrors('name');
    } else {
      reset();
      setValue('password', '');
      setValue('status', true);
    }
  }, [isOpen, userToEdit, setValue, reset, clearErrors]);

  const onFormSubmit = async (data) => {
    if (!data.name || data.name.trim() === '') {
      setMessage({ type: 'error', text: 'Name is required and cannot be empty' });
      return;
    }

    try {
      setSaveLoading(true);

      const formData = {
        ...data,
        id: userToEdit?.id || Date.now(),
        IsActive: data.status,
        permissions: {
          userManagement: data.userManagement,
          cropManagement: data.cropManagement,
          reportAccess: data.reportAccess,
          deleteAllowed: data.deleteAllowed,
        },
      };

      await onSubmit(formData, !!userToEdit);

      setMessage({
        type: 'success',
        text: userToEdit ? 'User updated successfully!' : 'User added successfully!'
      });

      setTimeout(() => {
        setMessage(null);
        reset();
        onClose();
        setSaveLoading(false);
      }, 2000);

    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save user. Please try again.' });
      setSaveLoading(false);
    }
  };



  return (
    <>
      <style>{customStyles}</style>
      <Drawer
        open={isOpen}
        // onClose={onClose}
        direction="right"
        size={900}
        style={{ zIndex: 1050 }}
      >
        <div className="relative">
          <div className="card-body">
            <button
              variant="outline"
              onClick={onClose}
              style={{ border: 'none', color: '#000', padding: 0, textAlign: 'end', padding: '15px', position: 'absolute', top: 0, right: 15 }}
            >
              <Icon icon="lucide:x" className="text-2xl" />
            </button>
            <h6 className="text-xxl text-primary-light mb-16">{userToEdit ? 'Edit User' : 'Add New User'}</h6>

            {message && (
              <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit(onFormSubmit)}>
              {/* Name + Surname */}
              <div className="form-grid-container">
                <div>
                  <label htmlFor="name" className="form-label">
                    Name <span className="text-danger-600">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'border-danger-600' : ''}`}
                    id="name"
                    placeholder="Enter Name"
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Name must be at least 2 characters' },
                      pattern: {
                        value: /^[A-Za-z\s]+$/,
                        message: 'Name must contain only letters and spaces',
                      },
                    })}
                    ref={(e) => {
                      register('name').ref(e);
                      nameInputRef.current = e;
                    }}
                  />
                  {errors.name && <span className="error-message">{errors.name.message}</span>}
                </div>
                <div>
                  <label htmlFor="surname" className="form-label">
                    Surname <span className="text-danger-600">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.surname ? 'border-danger-600' : ''}`}
                    id="surname"
                    placeholder="Enter Surname"
                    {...register('surname', {
                      required: 'Surname is required',
                      minLength: { value: 2, message: 'Surname must be at least 2 characters' },
                      pattern: {
                        value: /^[A-Za-z\s]+$/,
                        message: 'Surname must contain only letters and spaces',
                      },
                    })}
                  />
                  {errors.surname && <span className="error-message">{errors.surname.message}</span>}
                </div>
              </div>

              {/* Email + Phone */}
              <div className="form-grid-container">
                <div>
                  <label htmlFor="email" className="form-label">
                    Email ID <span className="text-danger-600">*</span>
                  </label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'border-danger-600' : ''}`}
                    id="email"
                    disabled={!!userToEdit}
                    placeholder="Enter Email ID"
                    {...register('email', {
                      required: 'Email ID is required',
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: 'Invalid email address',
                      },
                    })}
                  />
                  {errors.email && <span className="error-message">{errors.email.message}</span>}
                </div>
                <div>
                  <label htmlFor="phone" className="form-label">
                    Phone Number <span className="text-danger-600">*</span>
                  </label>
                  <input
                    type="tel"
                    className={`form-control ${errors.phone ? 'border-danger-600' : ''}`}
                    id="phone"
                    placeholder="Enter Phone Number"
                    {...register('phone', {
                      required: 'Phone Number is required',
                      pattern: {
                        value: /^\+?[\d\s-]{10,15}$/,
                        message: 'Phone number must be 10-15 digits, may include +, spaces, or dashes',
                      },
                    })}
                  />
                  {errors.phone && <span className="error-message">{errors.phone.message}</span>}
                </div>
              </div>

              {/* Address */}
              <div className="mb-20">
                <label htmlFor="address" className="form-label">Address</label>
                <textarea
                  id="address"
                  placeholder="Enter Address"
                  rows={5}
                  className={`form-control ${errors.address ? 'border-danger-600' : ''}`}
                  {...register('address', {
                    maxLength: { value: 500, message: 'Address must be less than 500 characters' },
                  })}
                />
                {errors.address && <span className="error-message">{errors.address.message}</span>}
              </div>

              {/* Permissions + Status */}
              <div className="form-grid-container mb-20">
                {user?.Role === "SuperAdmin" && (
                  <div className="permissions-group">
                    <h6 className="text-md text-primary-light mb-12">User Permissions</h6>
                    <div className="checkbox-grid">
                      <div className="form-check style-check">
                        <input
                          className="form-check-input radius-4 border border-neutral-400"
                          type="checkbox"
                          id="userManagement"
                          {...register('userManagement')}
                        />
                        <label htmlFor="userManagement">
                          Allow User Management
                        </label>
                      </div>
                      <div className="form-check style-check">
                        <input
                          className="form-check-input radius-4 border border-neutral-400"
                          type="checkbox"
                          id="cropManagement"
                          {...register('cropManagement')}
                        />
                        <label htmlFor="cropManagement">
                          Allow Crop Management
                        </label>
                      </div>
                      <div className="form-check style-check">
                        <input
                          className="form-check-input radius-4 border border-neutral-400"
                          type="checkbox"
                          id="reportAccess"
                          {...register('reportAccess')}
                        />
                        <label htmlFor="reportAccess">
                          Allow Report Access
                        </label>
                      </div>
                      <div className="form-check style-check">
                        <input
                          className="form-check-input radius-4 border border-neutral-400"
                          type="checkbox"
                          id="deleteAllowed"
                          {...register('deleteAllowed')}
                        />
                        <label htmlFor="deleteAllowed">
                          Allow Delete
                        </label>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <label htmlFor="status" className="form-label fw-semibold text-primary-light text-sm mb-8">Status</label>
                  <div className="form-check style-check">
                    <input
                      id="status"
                      type="checkbox"
                      className="form-check-input radius-4 border border-neutral-400"
                      disabled={loggedInUser && userToEdit && loggedInUser.EmailID === userToEdit.email}
                      {...register("status")}
                      onChange={(e) => {
                        setValue("status", e.target.checked)
                      }}
                    />
                    <label htmlFor="status">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="d-flex align-items-center justify-content-center gap-3">
                <button
                  type="button"
                  disabled={saveLoading}
                  className={`border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-10 radius-8`}
                  onClick={onClose}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saveLoading}
                  className="btn btn-yellow border border-600 text-md px-56 py-12 radius-8 d-flex align-items-center gap-2"
                >
                  {saveLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm"></span>
                      {userToEdit ? 'Updating...' : 'Saving...'}
                    </>
                  ) : (
                    userToEdit ? 'Update' : 'Save'
                  )}
                </button>

              </div>
            </form>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default AddAdminUser;